// src/components/ChargingStation/hooks/useStripeWeb.ts
import { useEffect, useRef, useState } from 'react';
import {
  loadStripe,
  Stripe,
  StripeAddressElement,
  StripeElements,
  StripePaymentElement,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { useSelector } from 'react-redux';
import { notificationService } from '../../../services/notification-service';
import { createOrder, cancelOrder, createFreeOrder } from '../../../services/orders-service';
import { isSuccessful } from '../../../services/api-requests';
import { getPaymentDetails } from '../../../services/payments-service';
import { ChargerAttributes, isChargerOffline } from '../../../models/charger-model';
import { OrderAttributes } from '../../../models/order-model';
import { ActiveChargeAttributes } from '../../../models/active-charges-model';
import { PaymentDetails } from '../../../models/payment-model';
import { RootState } from '../../../store';
import { isCurrentlyUnavailable } from '../../common/utils/unavailable-times-utils';
import { isDiscountEffectivelyFree } from '../../common/utils/get-price-info';

export const ACTIVE_CHARGE_BLOCK_MESSAGE =
  'You already have an active charging session. Finish or cancel it before checking out another charger.';

interface UseStripeWebOptions {
  selectedCharger: ChargerAttributes | null | undefined;
  onNewOrderCreated: (o: OrderAttributes) => void;
}

export function useStripeWeb({
  selectedCharger,
  onNewOrderCreated,
}: UseStripeWebOptions) {
  // ────────────────────────────────────────────────────────────────────────────────
  // State
  // ────────────────────────────────────────────────────────────────────────────────
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [stripeElements, setStripeElements] = useState<
    StripeElements | undefined
  >(undefined);
  const [stripePaymentElement, setStripePaymentElement] = useState<
    StripePaymentElement | undefined
  >(undefined);
  const [stripeAddressElement, setStripeAddressElement] = useState<
    StripeAddressElement | undefined
  >(undefined);

  const [order, setOrder] = useState<OrderAttributes | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentDetails | null>(null);
  const [paymentsDropdownOpen, setPaymentsDropdownOpen] =
    useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [chargerInUse, setChargerInUse] = useState<boolean>(false);
  const activeCharges: ActiveChargeAttributes[] | null = useSelector(
    (state: RootState) => state.user.activeCharges
  );
  const hasActiveCharge = !!activeCharges && activeCharges.length > 0;

  // Track last order id and debounce rapid updates
  const lastOrderIdRef = useRef<string | null>(null);
  const setOrderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasCreatedOrderRef = useRef<boolean>(false);

  const isFreeOverride = isDiscountEffectivelyFree(selectedCharger?.user_pricing_override);

  async function createFreeOrderForCharger(): Promise<OrderAttributes | null> {
    if (!selectedCharger?.uuid) return null;
    if (isChargerOffline(selectedCharger)) {
      console.log('[useStripeWeb] Charger offline, skipping free order creation');
      return null;
    }
    console.log('[useStripeWeb] Creating FREE order for charger:', selectedCharger.uuid);
    const resp = await createFreeOrder(selectedCharger.uuid);
    if (resp.status === 204 || resp.status === 409 || resp.status === 403) {
      const msg =
        (resp.payload as any)?.message ||
        (resp.status === 403
          ? 'The host has paused this charger. Please check back later.'
          : null);
      console.log('[useStripeWeb] Charger unavailable, cannot create free order:', resp.status, msg);
      setChargerInUse(true);
      setChargerUnavailableMessage(msg);
      return null;
    }
    if (isSuccessful(resp.status)) {
      console.log('[useStripeWeb] Free order created:', resp.payload?.id);
      setChargerInUse(false);
      return resp.payload!;
    }
    console.error('[useStripeWeb] Free order creation failed:', resp);
    return null;
  }

  const scheduleOrderUpdate = (next: OrderAttributes | null) => {
    if (setOrderTimerRef.current) {
      clearTimeout(setOrderTimerRef.current);
    }
    setOrderTimerRef.current = setTimeout(() => {
      if (next === null) {
        if (lastOrderIdRef.current !== null) {
          lastOrderIdRef.current = null;
          setOrder(null);
        }
        return;
      }
      if (lastOrderIdRef.current === next.id) {
        return; // equality guard by id
      }
      lastOrderIdRef.current = next.id;
      setOrder(next);
    }, 50);
  };

  // ────────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────────────────────────
  const [chargerUnavailableMessage, setChargerUnavailableMessage] = useState<string | null>(null);

  async function createOrderForCharger(): Promise<OrderAttributes | null> {
    if (!selectedCharger?.uuid) return null;
    if (isChargerOffline(selectedCharger)) {
      console.log('[useStripeWeb] Charger offline, skipping order creation');
      return null;
    }
    try {
      console.log('[useStripeWeb] Creating order for charger:', selectedCharger.uuid);
      const response = await createOrder(selectedCharger.uuid);
      
      // 204 = silently occupied, 409 = conflict (reconciliation pending or
      // charger offline), 403 = host has disabled the charger. All three mean
      // "unavailable" — show the banner with the backend's message instead of
      // silently hiding the checkout section.
      if (response.status === 204 || response.status === 409 || response.status === 403) {
        const msg =
          (response.payload as any)?.message ||
          (response.status === 403
            ? 'The host has paused this charger. Please check back later.'
            : null);
        console.log('[useStripeWeb] Charger unavailable:', response.status, msg);
        setChargerInUse(true);
        setChargerUnavailableMessage(msg);
        return null;
      }
      
      if (isSuccessful(response.status) && response.payload) {
        console.log('[useStripeWeb] Order created successfully:', response.payload.id);
        setChargerInUse(false);
        setChargerUnavailableMessage(null);
        return response.payload;
      }
      
      console.error('[useStripeWeb] Order creation failed:', response.status);
      return null;
    } catch (e) {
      console.error('[useStripeWeb] Exception creating order:', e);
      return null;
    }
  }

  async function fetchStripeInstance(): Promise<Stripe | null> {
    try {
      const s = await loadStripe(process.env.STRIPE_PUBLISHABLE_KEY!);
      return s;
    } catch (err) {
      console.error('Error loading Stripe:', err);
      setLoadError(true);
      return null;
    }
  }

  /**
   * Automatically use existing payment method if available
   */
  async function useExistingPaymentMethod(): Promise<void> {
    if (!stripe || !stripeElements || !order || !order.payment) {
      console.warn('Missing required data for automatic payment submission');
      return;
    }

    // If payment data already exists, we don't need to do anything
    if (paymentData) {
      console.log('Payment data already exists, no need for automatic payment');
      return;
    }

    try {
      console.log(
        'Automatically using existing payment method for order:',
        order.id
      );

      const confirmation = await stripe.confirmPayment({
        elements: stripeElements,
        clientSecret: order.payment.clientSecret,
        redirect: 'if_required',
        confirmParams: {},
      });

      console.log('Automatic payment confirmation result:', confirmation);

      // Handle payment confirmation status
      switch (confirmation?.paymentIntent?.status) {
        case 'requires_capture': {
          const paymentDetailsResponse = await getPaymentDetails(order.id);
          // Update payment data and UI state
          setPaymentData(paymentDetailsResponse.payload!);
          console.log('Automatic payment successful, payment data updated');
          break;
        }
        default:
          console.log(
            'Unhandled automatic payment status:',
            confirmation?.paymentIntent?.status
          );
      }
    } catch (err) {
      console.error('Error during automatic payment submission:', err);
      // If automatic payment fails, we don't want to block the user
      // They can still manually add/change payment method
    }
  }

  function mountElements(o: OrderAttributes, s: Stripe) {
    if (!o.payment) return;
    const elements = s.elements({
      clientSecret: o.payment.clientSecret,
      customerSessionClientSecret: o.payment.customerSessionClientSecret,
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary: '#3FC67D',
          colorBackground: '#E8E8E9',
          colorText: '#000000',
          colorTextSecondary: '#808080',
          colorTextPlaceholder: '#808080',
          colorDanger: '#ff0000',
        },
        labels: 'floating',
        rules: {
          '.CheckboxLabel': {
            color: '#808080',
          },
        },
      },
    });

    setStripeElements(elements);
    const paymentEl = elements.create(
      'payment',
      {} as StripePaymentElementOptions
    );
    const addressEl = elements.create('address', {
      mode: 'billing',
      allowedCountries: ['US'],
    });

    setStripePaymentElement(paymentEl);
    setStripeAddressElement(addressEl);
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Create + Load Stripe + Elements on mount (Web only)
  // ────────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || !window.document) return;

    if (!selectedCharger?.uuid) {
      console.log('[useStripeWeb] No selectedCharger, skipping order creation');
      return;
    }

    if (isChargerOffline(selectedCharger)) {
      console.log('[useStripeWeb] Charger offline or no live CSMS snapshot, skipping order creation');
      hasCreatedOrderRef.current = false;
      return;
    }

    if (isCurrentlyUnavailable(selectedCharger.unavailable_times ?? null)) {
      console.log('[useStripeWeb] Charger is currently unavailable, skipping order creation');
      return;
    }

    // A user with an active (or scheduled) charge cannot check out another
    // charger, so don't create a throwaway order that would later need to be
    // cancelled.
    if (hasActiveCharge) {
      console.log('[useStripeWeb] User already has an active charge, skipping order creation');
      setChargerInUse(true);
      setChargerUnavailableMessage(ACTIVE_CHARGE_BLOCK_MESSAGE);
      return;
    }

    if (order && order.chargerId === selectedCharger.uuid) {
      console.log('[useStripeWeb] Order already exists for this charger, skipping creation');
      return;
    }

    if (hasCreatedOrderRef.current) {
      console.log('[useStripeWeb] Order creation already in progress, skipping...');
      return;
    }

    hasCreatedOrderRef.current = true;

    if (isFreeOverride) {
      createFreeOrderForCharger()
        .then((o) => {
          if (!o) {
            if (!chargerInUse) setLoadError(true);
            hasCreatedOrderRef.current = false;
            return;
          }
          scheduleOrderUpdate(o);
          onNewOrderCreated(o);
        })
        .catch((err) => {
          console.error('Failed to create free order:', err);
          setLoadError(true);
          hasCreatedOrderRef.current = false;
        });
      return;
    }

    let orderData: OrderAttributes | null = null;
    let stripeInstance: Stripe | null = null;

    createOrderForCharger()
      .then((o) => {
        if (!o) {
          if (!chargerInUse) {
            setLoadError(true);
          }
          hasCreatedOrderRef.current = false;
          return null;
        }
        
        orderData = o;
        scheduleOrderUpdate(o);
        onNewOrderCreated(o);

        if (o.is_free_override || o.pricing_override_discount_type === 'free' || !o.payment) {
          console.log('[useStripeWeb] Backend returned a free order — skipping Stripe init');
          return null;
        }

        return fetchStripeInstance();
      })
      .then((s) => {
        if (!s) {
          if (orderData && !orderData.is_free_override && orderData.pricing_override_discount_type !== 'free' && orderData.payment) {
            setLoadError(true);
          }
          hasCreatedOrderRef.current = false;
          return;
        }
        stripeInstance = s;
        setStripe(s);

        // Now mount elements with the order and stripe instance
        if (orderData && stripeInstance && orderData.payment) {
          const elements = stripeInstance.elements({
            clientSecret: orderData.payment.clientSecret,
            customerSessionClientSecret:
              orderData.payment.customerSessionClientSecret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#3FC67D',
                colorBackground: '#E8E8E9',
                colorText: '#000000',
                colorTextSecondary: '#808080',
                colorTextPlaceholder: '#808080',
                colorDanger: '#ff0000',
              },
              labels: 'floating',
              rules: {
                '.CheckboxLabel': {
                  color: '#808080',
                },
              },
            },
          });

          setStripeElements(elements);
          const paymentEl = elements.create(
            'payment',
            {} as StripePaymentElementOptions
          );
          const addressEl = elements.create('address', {
            mode: 'billing',
            allowedCountries: ['US'],
          });

          setStripePaymentElement(paymentEl);
          setStripeAddressElement(addressEl);

          // Mount the elements immediately for automatic dropdown
          console.log('Mounting elements immediately for automatic dropdown');
          try {
            addressEl.mount('#address-element');
            paymentEl.mount('#payment-element');
            setPaymentsDropdownOpen(true);
            console.log('Dropdown opened successfully');
          } catch (error) {
            console.error('Error mounting Stripe elements:', error);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to load Stripe Elements or order:', err);
        setLoadError(true);
        // Reset flag on error so it can retry
        hasCreatedOrderRef.current = false;
      });
    
    // Note: DON'T reset hasCreatedOrderRef in cleanup!
    // That would allow React Strict Mode's double-invoke to create duplicate orders.
    // The ref persists across Strict Mode's unmount/remount cycle, which is what we want.
  }, [
    selectedCharger?.uuid,
    order,
    selectedCharger?.csms_live_status?.available,
    selectedCharger?.connected,
    hasActiveCharge,
  ]);

  // Cleanup debouncer on unmount
  useEffect(() => {
    return () => {
      if (setOrderTimerRef.current) {
        clearTimeout(setOrderTimerRef.current);
      }
    };
  }, []);

  // ────────────────────────────────────────────────────────────────────────────────
  // Handlers exposed to the screen
  // ────────────────────────────────────────────────────────────────────────────────

  /**
   * Called when user opens/closes the "Add payment method" dropdown on web.
   */
  function onDropdownPressed() {
    if (isFreeOverride) return;

    console.log('onDropdownPressed called', {
      stripePaymentElement: !!stripePaymentElement,
      stripeAddressElement: !!stripeAddressElement,
      paymentsDropdownOpen,
      order: !!order,
    });

    if (!stripePaymentElement || !stripeAddressElement) {
      console.warn('Stripe elements not ready yet');
      return;
    }

    if (paymentsDropdownOpen) {
      // Close dropdown
      stripePaymentElement.unmount();
      stripeAddressElement.unmount();
      setPaymentsDropdownOpen(false);
    } else {
      // Open dropdown
      try {
        stripeAddressElement.mount('#address-element');
        stripePaymentElement.mount('#payment-element');
        setPaymentsDropdownOpen(true);
      } catch (error) {
        console.error('Error mounting Stripe elements:', error);
      }
    }
  }

  /**
   * When user clicks "Submit" on Stripe Elements form:
   */
  async function onPaymentSubmit(): Promise<void> {
    if (isFreeOverride) return;

    console.log('onPaymentSubmit called', {
      stripe: !!stripe,
      stripeElements: !!stripeElements,
      order: !!order,
      orderId: order?.id,
      clientSecret: order?.payment?.clientSecret,
    });

    if (!stripe || !stripeElements || !order) {
      console.warn('Missing required data for payment submission');
      return;
    }

    try {
      if (!order.payment) return;

      const confirmation = await stripe.confirmPayment({
        elements: stripeElements,
        clientSecret: order.payment.clientSecret,
        redirect: 'if_required',
        confirmParams: {},
      });

      console.log('Payment confirmation result:', confirmation);

      switch (confirmation?.paymentIntent?.status) {
        case 'requires_capture': {
          const paymentDetailsResponse = await getPaymentDetails(order.id);
          setPaymentData(paymentDetailsResponse.payload!);
          setPaymentsDropdownOpen(false);
          console.log('Payment successful, payment data updated');
          break;
        }
        default:
          console.log(
            'Unhandled payment status:',
            confirmation?.paymentIntent?.status
          );
      }
    } catch (err) {
      console.error('Error during Stripe payment submission:', err);
    }
  }

  /**
   * If user clicks the "X" on the payment card, remove current payment method
   * and create a brand-new order.
   */
  async function handleRemovePayment() {
    if (!order) return;
    await cancelOrder(order.id);
    scheduleOrderUpdate(null);
    setPaymentData(null);
    onDropdownPressed(); // ensure elements are unmounted

    const newOrder = await createOrderForCharger();
    if (!newOrder) {
      setLoadError(true);
      return;
    }
    scheduleOrderUpdate(newOrder);
    onNewOrderCreated(newOrder);
  }

  /**
   * Automatically open payment dropdown for users with existing payment methods
   * This allows them to see their saved payment method and submit it immediately
   */
  async function openPaymentDropdownForExistingMethod(): Promise<void> {
    console.log(
      'openPaymentDropdownForExistingMethod called (legacy function)'
    );
    // This function is kept for compatibility but the logic is now in the useEffect
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Return everything the screen needs for "Web" payments
  // ────────────────────────────────────────────────────────────────────────────────
  return {
    // State
    isLoading: order === null && !loadError && !chargerInUse,
    order,
    paymentData,
    paymentsDropdownOpen,
    loadError,
    chargerInUse,
    chargerUnavailableMessage,
    reinitializeForNewOrder: (nextOrder: OrderAttributes) => {
      // Set your internal order state, update clientSecret/ephemeralKey, etc.
      // Example:
      setOrder(nextOrder);
      // setPaymentData({
      //   clientSecret: nextOrder.payment.clientSecret,   // adapt to your field names
      //   customerId:   nextOrder.payment.stripeCustomerId,
      //   ephemeralKey: nextOrder.payment.ephemeralKey,
      // });
    },

    // Elements
    stripe,
    stripeElements,
    stripePaymentElement,
    stripeAddressElement,

    // Handlers
    onDropdownPressed,
    onPaymentSubmit,
    handleRemovePayment,
    openPaymentDropdownForExistingMethod,
    isFreeOverride: isFreeOverride
      || order?.is_free_override === true
      || order?.pricing_override_discount_type === 'free',
  };
}
